import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { getProjectJobsByBatchQuery, getProjectJobsQuery } from '@/queries/project.query';
import { batchByproductsQuery } from '@/queries/byproduct.query';
import { reportTrackingAtom } from '@/atoms/report-progress.atom';

/**
 * A RENDER job that finished its work but could not draw the point clouds
 * records the reason on its output (`processRender/index.ts`) and still chains
 * the PDF — the documented non-fatal path of spec §13.7. Returns the reason,
 * or null when the job carries none.
 */
function readRenderError(outputJson: unknown): string | null {
  if (!outputJson || typeof outputJson !== 'object') return null;
  const value = (outputJson as { renderError?: unknown }).renderError;
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  // The worker can hand back a multi-line Python traceback; a toast gets the
  // headline only, the whole thing goes to the console at the call site.
  const firstLine = value.trim().split('\n')[0].trim();
  return firstLine.length > 160 ? `${firstLine.slice(0, 159)}…` : firstLine;
}

/**
 * Mounted once per project shell (alongside `useVectorExportProgress`). A
 * PDF_REPORT job produces a download-only byproduct (no viewable layer), so
 * success is detected by the parent job transitioning to PROCESSED. On success
 * the loading toast resolves into a success toast carrying a Download action
 * that lazily resolves the byproduct's presigned URL by `processJobId`.
 *
 * A per-station report *with* point-cloud renders (spec §13.7) is two jobs in
 * one batch: preprocess queues a RENDER job, and `processRender` chains the
 * PDF_REPORT job when it is done (rulings R5/R7). So the tracker follows both,
 * batch-scoped. A deviation or surface-difference run's own RENDER child
 * lands in that SAME batch as its chained report (`processDeviation/index.ts`),
 * so batch scoping alone cannot tell the two apart — the tracker only ever
 * looks for a RENDER row when it was seeded with `renderCount > 0`, which only
 * `GenerateReportDialog` sets, and only when the render checkbox was ticked.
 */
export function useReportProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(reportTrackingAtom);

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    // `cancelRefetch: false`: `refetch()` cancels the request still in
    // flight by default, so a response slower than this 2 s interval is
    // never delivered and the toast spins for ever. A tick JOINS the poll
    // already running (see `useDeviationProgress` for the reported case).
    const tick = () => void refetchJobs({ cancelRefetch: false });
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs]);

  useEffect(() => {
    if (!hasTrackers) return;
    const jobs = jobsData?.jobs ?? [];
    /** Batches whose RENDER job has to be asked for its `renderError`. The
     *  2 s poll no longer carries `outputJson` — it used to drag EVERY job's
     *  output through every tick, 7 MB of it on the project this was reported
     *  from — so the one field this warning needs is read per batch, once, at
     *  the hand-over, and never on the polling path. */
    const renderErrorChecks: string[] = [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // PDF_REPORT is the only leaf in its synthetic batch, so matching by
        // batchId (and type, defensively) is unambiguous. A per-station PDF
        // with renders is queued as a RENDER job that chains the PDF_REPORT
        // job on completion (spec §13.7, ruling R5), so for the first part of
        // the wait there IS no report job in this batch.
        //
        // The RENDER job is only ever consulted for a tracker that was seeded
        // expecting one (`renderCount > 0`, which only `GenerateReportDialog`
        // sets, and only when the user ticked the render box). A deviation or
        // surface-difference run drops its OWN render child into the very same
        // batch as its chained report (`processDeviation/index.ts`), so an
        // unconditional match would hijack those long-standing trackers.
        const renderCount = tracker.renderCount ?? 0;
        const jobItem = jobs.find((j) => j.batchId === tracker.batchId && j.type === 'PDF_REPORT');
        const renderItem =
          renderCount > 0 ? jobs.find((j) => j.batchId === tracker.batchId && j.type === 'RENDER') : undefined;

        if (!jobItem) {
          if (renderItem?.status === 'FAILED') {
            // Nothing will chain the report now — the tracker would spin
            // forever. A render failure INSIDE the worker is non-fatal and
            // never lands here (the PDF still prints, see below); this is the
            // job itself dying, which the user has to be told about — with a
            // next step, not with the Node stack `errorLogs` carries (see
            // `libs/common/src/job.ts`). The raw text stays in the console for
            // whoever debugs it.
            if (typeof renderItem.errorLogs === 'string' && renderItem.errorLogs.trim().length > 0) {
              console.error('[report] station render job failed', renderItem.id, renderItem.errorLogs.trim());
            }
            toast.error(t('report.renderFailedToast', { jobId: renderItem.id }), {
              ...(tracker.toastId !== undefined ? { id: tracker.toastId } : {}),
              duration: 10000,
            });
            next.splice(i, 1);
            changed = true;
            continue;
          }
          if (renderItem && tracker.stage !== 'rendering') {
            // The dialog already opens the toast on the render stage, so this
            // is the path for any other seeder (or a tracker restored without
            // a stage): switch it over once, then leave it alone.
            if (tracker.toastId !== undefined) {
              toast.loading(t('report.renderingToast', { count: renderCount, title: tracker.title }), {
                id: tracker.toastId,
                duration: Infinity,
              });
            }
            next[i] = { ...tracker, stage: 'rendering' };
            changed = true;
          }
          // Either the render is still running, or neither job is visible yet
          // (the batch was only just enqueued) — keep waiting either way.
          continue;
        }

        if (tracker.stage === 'rendering') {
          // The chain handed over. A non-fatal render failure gets its own
          // warning — the report has NOT failed, it just prints "Render
          // unavailable" on every page — and the loading toast goes back to
          // the PDF stage. The id is derived from the batch so re-running the
          // updater cannot stack two copies of the warning.
          if (renderItem) renderErrorChecks.push(tracker.batchId);
          if (tracker.toastId !== undefined && jobItem.status !== 'PROCESSED' && jobItem.status !== 'FAILED') {
            toast.loading(t('report.runningToast', { title: tracker.title }), {
              id: tracker.toastId,
              duration: Infinity,
            });
          }
          next[i] = { ...tracker, stage: 'pdf' };
          changed = true;
        }

        if (jobItem.status === 'FAILED') {
          const detail =
            typeof jobItem.errorLogs === 'string' && jobItem.errorLogs.trim().length > 0
              ? jobItem.errorLogs.trim()
              : null;
          toast.error(detail ?? t('report.failedToast'), {
            // Only attach `id` when present — without it sonner creates a
            // new toast (the deviation-modal path skips the loading toast).
            ...(tracker.toastId !== undefined ? { id: tracker.toastId } : {}),
            duration: 6000,
          });
          next.splice(i, 1);
          changed = true;
          continue;
        }

        if (jobItem.status === 'PROCESSED') {
          const completedJobId = jobItem.id;
          toast.success(t('report.completedToast', { title: tracker.title }), {
            // Only attach `id` when present — without it sonner creates a
            // new toast (the deviation-modal path skips the loading toast).
            ...(tracker.toastId !== undefined ? { id: tracker.toastId } : {}),
            duration: 10000,
            action: {
              label: t('report.download'),
              onClick: async () => {
                try {
                  // Resolve via the synthetic batch (by batchId). Match on
                  // processJobId first. The fallback can no longer be "the
                  // batch's single export byproduct": with renders the batch
                  // also holds the renders zip, registered BEFORE the PDF, so
                  // prefer a .pdf filename over whatever happens to be first.
                  const data = await execute(batchByproductsQuery, { id: tracker.batchId });
                  const bps = data.batch?.byproducts ?? [];
                  const bp =
                    bps.find((b) => b.processJobId === completedJobId) ??
                    bps.find((b) => b.filename.toLowerCase().endsWith('.pdf')) ??
                    bps[0];
                  if (bp) window.open(bp.downloadUrl, '_blank');
                  else toast.error(t('report.byproductNotFound'));
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : String(err));
                }
              },
            },
          });
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });

    // One batch-scoped read per hand-over, off the polling path. The warning
    // is keyed on the batch, so a repeated updater run (StrictMode, a second
    // poll landing in the same tick) collapses onto the same toast rather
    // than stacking. A failed read simply means no warning — the PDF itself
    // is tracked by the poll above and is not waiting on this.
    for (const batchId of new Set(renderErrorChecks)) {
      void execute(getProjectJobsByBatchQuery, { batchId })
        .then((data) => {
          const render = data.jobs?.find((j) => j.type === 'RENDER');
          const renderError = readRenderError(render?.outputJson);
          if (renderError === null) return;
          console.warn('[report] station renders unavailable', renderError);
          toast.warning(t('report.renderPartialToast', { reason: renderError }), {
            id: `${batchId}-render-warning`,
            duration: 10000,
          });
        })
        .catch(() => undefined);
    }
  }, [jobsData, hasTrackers, projectId, setTrackers, t]);
}
